import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { contentFor, type Product } from "@nh/shared";
import { Alert } from "@/shared/ui/core";
import {
  Combobox,
  FormRow,
  FormSection,
  NumberField,
  RepeatableList,
  type ComboboxOption,
} from "@/features/admin/components";
import { useAdminProducts } from "../products.queries";
import type { ProductFormValues } from "../productForm";

/**
 * ═══════════════════════════════════════════════════════════════════
 * QUÉ LLEVA DENTRO un paquete: los productos y cuántas unidades.
 *
 * Decisión de Anahí (26-ago): la composición se arma en el formulario de
 * PRODUCTO, no en la sección de paquetes. `/admin/packages` sigue viva y
 * sin tocar mientras se planifica su retirada — hoy conviven, y el campo
 * `items` tiene la MISMA forma en los dos contratos justamente para que
 * migrar sea copiar y no traducir.
 *
 * ── Qué productos se pueden elegir ──────────────────────────────────
 * Los PUBLICADOS y los BORRADORES. Los archivados no.
 *
 * Los borradores entran a propósito: un paquete se arma para un
 * lanzamiento, y en un lanzamiento sus componentes todavía no están
 * publicados. Exigir que lo estén invierte el orden del trabajo —habría
 * que publicar seis productos sueltos para poder armar el paquete que es
 * lo único que se quiere vender— y publicar un producto "solo para
 * poder" es exactamente cómo aparece media ficha en la tienda.
 *
 * Los archivados quedan fuera porque archivado significa "ya no se
 * vende", y meterlo en un paquete nuevo sería venderlo otra vez por la
 * puerta de atrás. Uno que YA estaba en la lista se queda: ver abajo.
 *
 * ── Y qué pasa cuando un componente se archiva DESPUÉS ──────────────
 * Nada, y es deliberado. El servidor no toca los paquetes que lo
 * contienen —igual que archivar una categoría no reclasifica los
 * productos que la usan— porque una baja en cascada reescribiría en
 * silencio lo que se vende. Aquí la línea se sigue viendo, con el
 * producto marcado como archivado, y se puede quitar a mano.
 *
 * Lo que falta y queda anotado, porque no es esta tarea: la ficha
 * pública del paquete debería avisar de que uno de sus componentes está
 * archivado, y el día que el precio del paquete se derive de sus partes
 * habrá que decidir si un componente archivado cuenta.
 *
 * ── El duplicado no se puede ni teclear ─────────────────────────────
 * Los productos ya elegidos salen deshabilitados en el selector, con el
 * motivo escrito. El esquema del formulario y el servidor lo comprueban
 * igualmente —una lista de opciones no es una garantía— pero la primera
 * defensa es que la opción no se pueda pulsar.
 * ═══════════════════════════════════════════════════════════════════
 */
export function ProductPackageItems() {
  const { t, i18n } = useTranslation();
  const { id: editingId } = useParams<{ id: string }>();
  const { formState } = useFormContext<ProductFormValues>();
  const items = (useWatch({ name: "items" }) ?? []) as ProductFormValues["items"];

  const locale = i18n.language.startsWith("en") ? "en" : "es";

  /*
   * Se piden hasta cien de una vez y el filtrado ocurre en el navegador.
   *
   * Es lo correcto para el tamaño real de este catálogo —decenas de
   * productos— y evita una petición por tecla. El día que pase de cien,
   * lo que hay que cambiar es esto por una búsqueda contra el servidor
   * (`?search=`), que el API ya soporta; el `Combobox` no se entera,
   * porque solo recibe una lista de opciones.
   */
  const catalog = useAdminProducts({
    page: 1,
    perPage: 100,
    sort: "recommended",
  });

  const elegidos = new Set(
    items.map((item) => item.productId).filter((value) => value !== ""),
  );

  const options = useMemo<ComboboxOption[]>(() => {
    const productos = catalog.data?.items ?? [];

    return productos
      .filter((product: Product) => {
        // Un archivado solo se enseña si YA estaba en la lista: si no,
        // la línea guardada se quedaría sin nombre y parecería vacía.
        if (product.status === "archived") return elegidos.has(product.id);
        return true;
      })
      .map((product: Product) => {
        const content = contentFor(product.translations, locale);
        const yaEsta = elegidos.has(product.id);
        const esElMismo = editingId !== undefined && product.id === editingId;

        return {
          value: product.id,
          label: content.name,
          hint: [
            product.category,
            product.status === "draft" ? t("Borrador") : undefined,
            product.status === "archived" ? t("Archivado") : undefined,
          ]
            .filter(Boolean)
            .join(" · "),
          // El propio producto que se está editando NO se puede elegir:
          // es el ciclo que de verdad ocurre, porque el selector lo
          // ofrece con el mismo nombre que se está escribiendo arriba.
          disabled: esElMismo || yaEsta,
          disabledReason: esElMismo
            ? t("Un paquete no puede contenerse a sí mismo")
            : yaEsta
              ? t("Ya está en la lista")
              : undefined,
        };
      });
  }, [catalog.data, elegidos, editingId, locale, t]);

  const listError = formState.errors.items?.message;

  return (
    <FormSection
      title={t("Qué lleva el paquete")}
      /*
        Sin explicación: el título ya lo dice y la lista vacía lo repite
        ("Todavía no lleva nada. Añade el primer producto."). Medido, ese
        renglón eran los 30 px que dejaban el paso en 902 con el paquete
        recién elegido — dos píxeles fuera de la pantalla, que es una
        barra de desplazamiento entera por una frase que sobra.
      */
      // Una columna: cada línea es un selector ancho y un número, y en un
      // tercio de fila el nombre de un producto no cabe.
      columns={1}
      compact
    >
      {catalog.isError && (
        <Alert variant="warning">
          {t("No se pudo cargar el catálogo. Vuelve a intentarlo para elegir productos.")}
        </Alert>
      )}

      <RepeatableList
        name="items"
        max={60}
        addLabel={t("Añadir producto")}
        itemLabel={(index) => t("Producto {{n}}", { n: index + 1 })}
        emptyHint={t("Todavía no lleva nada. Añade el primer producto.")}
        error={typeof listError === "string" ? listError : undefined}
        newItem={() => ({ productId: "", quantity: "1" })}
      >
        {({ index, name }) => (
          /*
            El selector ocupa la fila y la cantidad va a su lado en
            pantalla ancha: el nombre de un producto necesita el espacio y
            "2" no.
          */
          <FormRow className="sm:grid-cols-[1fr_7rem]">
            <Controller
              name={`${name}.productId`}
              render={({ field, fieldState }) => (
                <Combobox
                  label={t("Producto")}
                  options={options}
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={field.onChange}
                  placeholder={t("Busca por nombre…")}
                  disabled={catalog.isPending || formState.isSubmitting}
                  error={
                    typeof fieldState.error?.message === "string"
                      ? fieldState.error.message
                      : undefined
                  }
                />
              )}
            />

            <NumberField
              name={`${name}.quantity`}
              label={t("Unidades")}
              placeholder="1"
              aria-label={t("Unidades del producto {{n}}", { n: index + 1 })}
            />
          </FormRow>
        )}
      </RepeatableList>
    </FormSection>
  );
}
